import React, { useState, useMemo, useCallback } from "react";
import {
  Box,
  Button,
  Modal,
  Typography,
  Grid,
  Stack,
  IconButton,
  CircularProgress,
  TextField,
  MenuItem,
  Divider,
} from "@mui/material";
import { Add, Remove } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import { listAllActivities, resetAllActivities } from "../../../store/actions";
import { tokens } from "../../../theme";
import { post } from "../../../services";
import { ADD_EXTRA_INVOICE } from "../../../graphql";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useProSidebar } from "react-pro-sidebar";
import {
  TAX_RATES,
  activityLineTotal,
  activityQtyLabel,
  activityRateLabel,
  activityStepperBounds,
  isPerUnitActivity,
  paxLabel,
  stepPaxCount,
  unitCapacityLabel,
} from "../../../utils";

// per_unit steppers count units: no minimum-pax jump and no maximum-pax clamp.
const stepActivityCount = (activity, current, increment) => {
  if (!isPerUnitActivity(activity)) {
    return stepPaxCount(current, activity.minimum_pax, activity.maximum_pax, increment);
  }
  const bounds = activityStepperBounds(activity);
  return stepPaxCount(current, bounds.jumpMin, bounds.max, increment);
};

const AddExtraBillModal = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const { collapsed } = useProSidebar();

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState({});
  const [customer, setCustomer] = useState({ name: "", email: "", mobile: "" });
  const [pmtStatus, setPmtStatus] = useState("unpaid");

  const { allActivities, loading, gstEnabled } = useSelector(
    ({ activities, registration }) => ({
      allActivities: activities?.activities?.list || [],
      loading: activities?.activities?.loading || false,
      gstEnabled: registration?.hotelProfile?.gst_enabled ?? false,
    })
  );

  // Extra-invoice is a new booking action — inactive activities shouldn't be
  // offered, same rule already applied in AddUpdateActivitiesButton.jsx.
  const activitiesList = useMemo(
    () => allActivities.filter((activity) => activity.is_active !== false),
    [allActivities]
  );

  const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: collapsed ? "80vw" : "65vw",
    maxHeight: "85vh",
    overflowY: "auto",
    bgcolor: colors.primary[400],
    borderRadius: 2,
    boxShadow: 24,
    p: 3,
  };

  const handleOpen = () => {
    setOpen(true);
    dispatch(resetAllActivities());
    dispatch(listAllActivities());
  };

  const handleClose = () => {
    setOpen(false);
    setSelected({});
    setCustomer({ name: "", email: "", mobile: "" });
    setPmtStatus("unpaid");
  };

  const handleIncrement = useCallback((activity) => {
    setSelected((prev) => {
      const current = prev[activity._id] || 0;
      const next = stepActivityCount(activity, current, true);
      return next === current ? prev : { ...prev, [activity._id]: next };
    });
  }, []);

  const handleDecrement = useCallback((activity) => {
    setSelected((prev) => {
      const current = prev[activity._id] || 0;
      const next = stepActivityCount(activity, current, false);
      if (next === current) return prev;
      const updated = { ...prev };
      if (next <= 0) delete updated[activity._id];
      else updated[activity._id] = next;
      return updated;
    });
  }, []);

  const totals = useMemo(() => {
    let subtotal = 0;

    activitiesList.forEach((a) => {
      if (selected[a._id]) {
        subtotal += activityLineTotal(a, selected[a._id]);
      }
    });

    if (!gstEnabled) {
      return {
        subtotal,
        tax: 0,
        total: subtotal,
      };
    }

    const tax = subtotal * TAX_RATES.ACTIVITY.RATE;

    return {
      subtotal,
      tax,
      total: subtotal + tax,
    };
  }, [activitiesList, selected, gstEnabled]);

  // Sort activities (selected first)
  const sortedActivities = useMemo(() => {
    return [...activitiesList].sort((a, b) => {
      const countA = selected[a._id] || 0;
      const countB = selected[b._id] || 0;
      return countB - countA;
    });
  }, [activitiesList, selected]);

  const handleSave = async () => {
    if (!customer.name.trim()) {
      toast.error("Customer Name is required.");
      return;
    }

    const selectedList = activitiesList
      .filter((a) => selected[a._id])
      .map((a) => ({
        _id: a._id,
        name: a.name,
        count: selected[a._id],
      }));

    if (!selectedList.length) {
      toast.error("Please select at least one activity.");
      return;
    }

    const invoiceData = {
      customer_details: customer,
      pmt_status: pmtStatus,
      activities: selectedList,
    };

    try {
      const apiResponse = await post("/graphql", {
        query: ADD_EXTRA_INVOICE,
        variables: invoiceData,
      });

      const invoice = apiResponse?.data?.data?.add_extra_invoice;

      if (invoice?._id) {
        toast.success("Invoice created successfully!");
        handleClose();
        navigate(`/billing/extras/${invoice._id}`);
      } else {
        toast.error("Failed to create invoice.");
      }
    } catch (error) {
      console.error(error);
      toast.error("Error creating invoice.");
    }
  };

  return (
    <>
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button variant="contained" onClick={handleOpen}>
          Add Extras Bill
        </Button>
      </Box>

      <Modal open={open} onClose={handleClose}>
        <Box sx={modalStyle}>
          <Typography variant="h6" mb={2}>
            Add Extra Activities Invoice
          </Typography>

          <CustomerForm
            customer={customer}
            setCustomer={setCustomer}
            pmtStatus={pmtStatus}
            setPmtStatus={setPmtStatus}
          />

          <Divider sx={{ my: 2 }} />

          {loading ? (
            <Stack alignItems="center" py={5}>
              <CircularProgress />
              <Typography mt={2}>Loading activities...</Typography>
            </Stack>
          ) : (
            <>
              <Grid container spacing={2}>
                {sortedActivities.map((activity) => (
                  <ActivityCard
                    key={activity._id}
                    activity={activity}
                    count={selected[activity._id] || 0}
                    onIncrement={handleIncrement}
                    onDecrement={handleDecrement}
                    theme={theme}
                  />
                ))}
              </Grid>

              <TotalsBox
                totals={totals}
                colors={colors}
                gstEnabled={gstEnabled}
              />
            </>
          )}

          <Stack mt={3} direction="row" justifyContent="flex-end" spacing={2}>
            <Button variant="outlined" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleSave}
              disabled={!Object.keys(selected).length || !customer.name}
            >
              Save Invoice
            </Button>
          </Stack>
        </Box>
      </Modal>
    </>
  );
};

export default AddExtraBillModal;

/* ---------------- COMPONENTS ---------------- */

const CustomerForm = ({ customer, setCustomer, pmtStatus, setPmtStatus }) => (
  <Box mb={3}>
    <Grid container spacing={2}>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          label="Customer Name"
          value={customer.name}
          onChange={(e) => setCustomer({ ...customer, name: e.target.value })}
          required
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          label="Email"
          value={customer.email}
          onChange={(e) => setCustomer({ ...customer, email: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          label="Mobile"
          value={customer.mobile}
          onChange={(e) => setCustomer({ ...customer, mobile: e.target.value })}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          select
          label="Payment Status"
          value={pmtStatus}
          onChange={(e) => setPmtStatus(e.target.value)}
        >
          <MenuItem value="paid">Paid</MenuItem>
          <MenuItem value="unpaid">Unpaid</MenuItem>
        </TextField>
      </Grid>
    </Grid>
  </Box>
);


const TotalsBox = ({ totals, colors, gstEnabled }) => (
  <Box mt={3} p={2} borderRadius={1} bgcolor={colors.primary[500]}>
    <Typography>Subtotal: ₹{totals.subtotal.toFixed(2)}</Typography>

    {gstEnabled && (
      <Typography>
        Tax ({TAX_RATES.ACTIVITY.PERCENT}%): ₹{totals.tax.toFixed(2)}
      </Typography>
    )}

    <Typography variant="h6" color="secondary">
      Total: ₹{totals.total.toFixed(2)}
    </Typography>
  </Box>
);



const ActivityCard = ({ activity, count, onIncrement, onDecrement, theme }) => {
  const perUnit = isPerUnitActivity(activity);
  const bounds = activityStepperBounds(activity);

  return (
    <Grid item xs={12} sm={6} md={4} key={activity._id}>
      <Stack
        p={2}
        borderRadius={1}
        bgcolor={
          count > 0
            ? theme.palette.primary.light
            : theme.palette.background.default
        }
        spacing={1}
        border={`1px solid ${theme.palette.divider}`}
      >
        <Typography fontWeight={600}>{activity.name}</Typography>
        {(perUnit || activity.minimum_pax > 1 || activity.maximum_pax > 0) && (
          <Typography variant="caption" color="text.secondary">
            {perUnit
              ? unitCapacityLabel(activity.maximum_pax || 0)
              : paxLabel(activity.minimum_pax || 1, activity.maximum_pax || 0)}
          </Typography>
        )}
        {activity.description && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={activity.description}
          >
            {activity.description}
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary">
          ₹{activity.rate} {activityRateLabel(activity)}
        </Typography>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="center"
          mt={1}
        >
          <IconButton
            size="small"
            onClick={() => onDecrement(activity)}
            disabled={count === 0}
          >
            <Remove fontSize="small" />
          </IconButton>
          <Typography fontWeight={600} minWidth={40} textAlign="center">
            {perUnit && count > 0 ? activityQtyLabel(activity, count) : count}
          </Typography>
          <IconButton
            size="small"
            onClick={() => onIncrement(activity)}
            disabled={
              perUnit
                ? count >= bounds.max
                : activity.maximum_pax > 0 && count >= activity.maximum_pax
            }
          >
            <Add fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>
    </Grid>
  );
};